/**
 * Visual Hierarchy Engine — ranks every object on the page.
 *
 * Takes the focal decision and produces an explicit priority ladder with
 * emphasis weights (0–1) that downstream systems translate into size,
 * weight, and contrast. Rule of thumb baked in: priorities fall off
 * steeply — a page where rank 1 and rank 3 feel similar has no hierarchy.
 */

/** Every rankable object role, in default reading order. */
export const RANKABLE_ROLES = [
  'hero', 'primaryHeading', 'secondaryHeading', 'body', 'offer', 'cta', 'footer',
];

/** Default emphasis ladder by rank position (steep falloff). */
const EMPHASIS_BY_RANK = [1, 0.72, 0.5, 0.34, 0.22, 0.14, 0.08];

/**
 * Build the hierarchy for a focus decision + brand style.
 * Returns ordered [{ role, priority, emphasis, typographyEmphasis }].
 */
export const rankObjects = ({ focus, style }) => {
  // Start from the focal ladder: primary first, supporting next (their
  // relative order follows reading order), then the deemphasized tail.
  const ordered = [
    focus.primary,
    ...RANKABLE_ROLES.filter((r) => focus.supporting.includes(r)),
    ...RANKABLE_ROLES.filter(
      (r) => r !== focus.primary && !focus.supporting.includes(r)
    ),
  ];

  return ordered.map((role, i) => ({
    role,
    priority: i + 1,
    emphasis: EMPHASIS_BY_RANK[i] ?? 0.05,
    // Text objects inherit the style's typographic loudness, scaled by rank.
    typographyEmphasis:
      role === 'hero'
        ? 0
        : Number((style.typographyEmphasis * (EMPHASIS_BY_RANK[i] ?? 0.05)).toFixed(3)),
  }));
};

/**
 * Sanity check a hierarchy: exactly one rank-1, steep falloff preserved.
 * Returns { valid, errors[] }.
 */
export const validateHierarchy = (ranks) => {
  const errors = [];
  if (ranks.filter((r) => r.priority === 1).length !== 1) {
    errors.push('hierarchy must have exactly one priority-1 object');
  }
  for (let i = 1; i < ranks.length; i++) {
    if (ranks[i].emphasis >= ranks[i - 1].emphasis) {
      errors.push(`emphasis must strictly fall: "${ranks[i].role}" >= "${ranks[i - 1].role}"`);
    }
  }
  return { valid: errors.length === 0, errors };
};
